"use client";

/**
 * AdminModal — 화면 중앙 모달 (우선순위 9)
 *
 * 제목/설명/본문/푸터를 props 로 받아 일관된 모달을 만든다. 제어 컴포넌트.
 * 등록/수정 폼, 안내 등에 사용한다. (확인 전용 다이얼로그는 ConfirmDialog 사용 예정)
 *
 * @example
 * <AdminModal
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="회원 등록"
 *   description="새 회원 정보를 입력하세요."
 *   footer={
 *     <>
 *       <Button variant="outline" onClick={() => setOpen(false)}>취소</Button>
 *       <Button onClick={save}>저장</Button>
 *     </>
 *   }
 * >
 *   ...폼 본문...
 * </AdminModal>
 */
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export type AdminModalSize = "sm" | "md" | "lg" | "xl" | "xxl" | "xxxl";

export interface AdminModalProps {
  open: boolean;
  /** 모달이 닫힐 때 호출되는 콜백 (X/오버레이/ESC/취소 등 모든 닫힘에 공통) */
  onClose: () => void;
  /** 모달 제목 */
  title?: ReactNode;
  /** 제목 아래 보조 설명 */
  description?: ReactNode;
  /** 본문 */
  children?: ReactNode;
  /** 하단 버튼 영역 (취소/저장 등) */
  footer?: ReactNode;
  /** 너비 (기본 md, xl=2xl, xxl=4xl, xxxl=6xl) */
  size?: AdminModalSize;
  /** 오버레이 클릭/ESC 로 닫기 막기 */
  disableClose?: boolean;
  /** 우상단 X 닫기 버튼 숨기기 */
  hideCloseButton?: boolean;
  /**
   * 모달이 열릴 때 내부 첫 번째 포커스 가능한 요소(예: 폼의 첫 select)로 자동 포커스할지 여부.
   * 기본 false — 첫 필드가 검색형 select(AdminSelect)인 경우 포커스만으로 드롭다운이
   * 자동으로 열려버리는 것을 막기 위한 기본값이다. 키보드 진입 편의를 위해 자동 포커스가
   * 필요하면 true로 켠다.
   */
  autoFocus?: boolean;
  className?: string;
}

const SIZE: Record<AdminModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  xxl: "max-w-4xl",
  xxxl: "max-w-6xl",
};

export function AdminModal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  disableClose = false,
  hideCloseButton = false,
  autoFocus = false,
  className,
}: AdminModalProps) {
  // className 으로 너비(w-*/max-w-*)를 직접 주면 size 기본 max-w 가 캡(cap)하지 않도록 끈다.
  const hasCustomWidth = !!className && /(?:^|\s)(?:max-)?w-/.test(className);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showClose={!hideCloseButton && !disableClose}
        // 콘텐츠가 길면 본문만 스크롤되도록 전체 높이를 제한한다. 헤더/본문/푸터 각자 여백을 관리하도록 기본 p-6·gap-4 는 없앤다.
        className={cn(
          "flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0",
          // 커스텀 너비가 있으면 size·기본 max-w 캡을 풀고(max-w-none), 없으면 size 적용
          hasCustomWidth ? "max-w-none" : SIZE[size],
          className
        )}
        onInteractOutside={
          disableClose ? (e) => e.preventDefault() : undefined
        }
        onEscapeKeyDown={disableClose ? (e) => e.preventDefault() : undefined}
        onOpenAutoFocus={autoFocus ? undefined : (e) => e.preventDefault()}
      >
        {(title || description) && (
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && (
              // asChild + div: description 에 div/span 등 블록 요소가 들어와도
              // <p> 안에 블록 요소가 들어가는 HTML 오류(hydration mismatch)가 나지 않도록 한다.
              <DialogDescription asChild>
                <div>{description}</div>
              </DialogDescription>
            )}
          </DialogHeader>
        )}

        {/* 본문: 길어지면 여기만 스크롤되고 헤더/푸터는 고정된다. 배경을 page 본문 영역과 같은 bg-muted/40 으로 둬서 카드형 폼과 구분되게 한다. */}
        <div className="flex-1 overflow-y-auto bg-muted/40 px-6 py-4">
          {children}
        </div>

        {footer && (
          <DialogFooter className="shrink-0 border-t px-6 py-4">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
